import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiCheck, FiMinus, FiChevronRight, FiChevronDown, FiList, FiFileText, FiFolder } from 'react-icons/fi';
import { crosswalkApi } from '../../../services/api';

// Nested source picker for the crosswalk. Two things are selectable:
//
//   Assessment                  (selected by assessment_id)
//     └ Documents
//         └ Document            (selected by evidence_document_key)
//   Other documents             (top-level `unbound_documents` — account/vendor evidence that
//     └ Document                  isn't attached to any assessment)
//
// Selecting an assessment takes its evidence with it; a document can also be picked on its own.
// The assessment row shows an indeterminate mark whenever those two disagree.

const KEY_SEP = '::';

const normalizeDocument = (d) => ({
  key: d.evidence_document_key,
  title: d.doc_title || d.evidence_document_key,
  docType: d.doc_type || null,
});

const normalize = (data, targetAssessmentIds) => {
  const targetKeys = new Set((targetAssessmentIds || []).map(id => String(id)));
  const assessments = (data?.assessments || [])
    // The target can't be its own source; the API includes it in the list.
    .filter(a => !targetKeys.has(String(a.assessment_id)))
    .map(a => {
      const seen = new Set();
      return {
        id: a.assessment_id,
        label: (a.assessment_description || `Assessment #${a.assessment_id}`).trim(),
        answerCount: a.answer_count ?? 0,
        documents: (a.documents || [])
          .filter(d => {
            if (!d.evidence_document_key || seen.has(d.evidence_document_key)) return false;
            seen.add(d.evidence_document_key);
            return true;
          })
          .map(normalizeDocument),
      };
    });

  const unboundDocuments = (data?.unbound_documents || [])
    .filter(d => d.evidence_document_key)
    .map(normalizeDocument);

  return { assessments, unboundDocuments };
};


// 'checked' when every key is selected, 'indeterminate' when some are, else 'unchecked'.
const groupState = (keys, selected) => {
  if (keys.length === 0) return 'unchecked';
  let hit = 0;
  keys.forEach(k => { if (selected.has(k)) hit += 1; });
  if (hit === 0) return 'unchecked';
  return hit === keys.length ? 'checked' : 'indeterminate';
};

const combineStates = (states) => {
  const real = states.filter(s => s !== null);
  if (real.length === 0) return 'unchecked';
  if (real.every(s => s === 'checked')) return 'checked';
  if (real.every(s => s === 'unchecked')) return 'unchecked';
  return 'indeterminate';
};

const CheckMark = ({ state }) => (
  <div className={`w-4 h-4 border flex items-center justify-center flex-shrink-0 ${
    state === 'unchecked'
      ? 'border-[#919191]/50 bg-white'
      : 'bg-[#0077b5] border-[#0077b5]'
  }`}>
    {state === 'checked' && <FiCheck className="text-white text-xs" />}
    {state === 'indeterminate' && <FiMinus className="text-white text-xs" />}
  </div>
);

const Row = ({ depth = 0, state, onToggle, expandable, expanded, onToggleExpand, icon, children, meta }) => (
  <div
    onClick={onToggle}
    style={{ paddingLeft: `${12 + depth * 16}px` }}
    className={`flex items-center gap-2 pr-3 py-2.5 border-b last:border-b-0 cursor-pointer transition-colors ${
      state === 'unchecked'
        ? 'border-[#919191]/20 text-[#919191] hover:bg-slate-50'
        : 'bg-[#0077b5]/10 border-[#0077b5]/20 text-[#53545a]'
    }`}
  >
    {expandable ? (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
        className="p-0.5 -ml-1 text-[#919191] hover:text-[#53545a] flex-shrink-0"
        aria-label={expanded ? 'Collapse' : 'Expand'}
      >
        {expanded ? <FiChevronDown className="text-sm" /> : <FiChevronRight className="text-sm" />}
      </button>
    ) : (
      <span className="w-[18px] flex-shrink-0" />
    )}
    <CheckMark state={state} />
    {icon}
    <div className="flex-1 min-w-0">{children}</div>
    {meta && <span className="text-xs text-[#919191] flex-shrink-0">{meta}</span>}
  </div>
);

const SourceDataSelector = ({ isOpen, targetAssessmentIds, onSelectionChange }) => {
  const [tree, setTree] = useState({ assessments: [], unboundDocuments: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const cache = useRef({});

  const [selectedAssessmentIds, setSelectedAssessmentIds] = useState(() => new Set());
  const [selectedDocumentKeys, setSelectedDocumentKeys] = useState(() => new Set());
  const [expanded, setExpanded] = useState(() => new Set());

  // Stable cache key for dependency tracking
  const targetKey = useMemo(
    () => (targetAssessmentIds && targetAssessmentIds.length > 0)
      ? targetAssessmentIds.slice().sort().join(',')
      : null,
    [targetAssessmentIds]
  );

  const toggleExpanded = (key) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Fetch the source data for the assessment being shared.
  useEffect(() => {
    if (!isOpen || !targetKey) {
      setTree({ assessments: [], unboundDocuments: [] });
      setError(false);
      return undefined;
    }
    if (cache.current[targetKey]) {
      setTree(cache.current[targetKey]);
      setError(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    crosswalkApi.getIndexSourceData(targetAssessmentIds)
      .then(data => {
        const resolved = normalize(data, targetAssessmentIds);
        cache.current[targetKey] = resolved;
        if (!cancelled) setTree(resolved);
      })
      .catch(err => {
        console.error('Failed to load source data for assessments', targetAssessmentIds, err);
        if (!cancelled) {
          setTree({ assessments: [], unboundDocuments: [] });
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isOpen, targetKey, targetAssessmentIds]);

  // Everything the crosswalk may draw from is selected by default; the user narrows it down
  // from there. Re-runs whenever the source set changes (target changed, modal reopened).
  useEffect(() => {
    setSelectedAssessmentIds(new Set(tree.assessments.map(a => a.id)));
    setSelectedDocumentKeys(new Set([
      ...tree.assessments.flatMap(a => a.documents.map(d => d.key)),
      ...tree.unboundDocuments.map(d => d.key),
    ]));
    setExpanded(new Set());
  }, [tree]);

  // Publish the selection upward as plain arrays.
  useEffect(() => {
    if (!onSelectionChange) return;
    onSelectionChange({
      assessmentIds: [...selectedAssessmentIds],
      documentKeys: [...selectedDocumentKeys],
    });
  }, [selectedAssessmentIds, selectedDocumentKeys, onSelectionChange]);

  const setToggle = (setter, keys, shouldSelect) => {
    setter(prev => {
      const next = new Set(prev);
      keys.forEach(k => { if (shouldSelect) next.add(k); else next.delete(k); });
      return next;
    });
  };

  const toggleDocuments = (keys) => {
    const allSelected = keys.length > 0 && keys.every(k => selectedDocumentKeys.has(k));
    setToggle(setSelectedDocumentKeys, keys, !allSelected);
  };

  // An assessment carries its evidence: selecting it selects its documents too.
  const toggleAssessment = (assessment) => {
    const keys = assessment.documents.map(d => d.key);
    const fully =
      selectedAssessmentIds.has(assessment.id) && keys.every(k => selectedDocumentKeys.has(k));
    setToggle(setSelectedAssessmentIds, [assessment.id], !fully);
    setToggle(setSelectedDocumentKeys, keys, !fully);
  };

  const allDocumentKeys = useMemo(
    () => [
      ...tree.assessments.flatMap(a => a.documents.map(d => d.key)),
      ...tree.unboundDocuments.map(d => d.key),
    ],
    [tree]
  );

  const selectAll = () => {
    setSelectedAssessmentIds(new Set(tree.assessments.map(a => a.id)));
    setSelectedDocumentKeys(new Set(allDocumentKeys));
  };
  const deselectAll = () => {
    setSelectedAssessmentIds(new Set());
    setSelectedDocumentKeys(new Set());
  };

  const hasRows = tree.assessments.length > 0 || tree.unboundDocuments.length > 0;

  const assessmentState = (a) =>
    combineStates([
      selectedAssessmentIds.has(a.id) ? 'checked' : 'unchecked',
      a.documents.length > 0 ? groupState(a.documents.map(d => d.key), selectedDocumentKeys) : null,
    ]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-[#919191]">
          Source Data
          {tree.assessments.length > 0 && ` (${tree.assessments.length})`}
          {(selectedAssessmentIds.size > 0 || selectedDocumentKeys.size > 0) && (
            <span className="ml-2 text-[#0b79b8] font-semibold">
              • {selectedAssessmentIds.size} selected, {selectedDocumentKeys.size} docs
            </span>
          )}
        </h3>
        {hasRows && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={selectAll}
              className="text-xs font-medium text-[#0077b5] hover:text-[#005a8c] transition-colors"
            >
              Select All
            </button>
            <span className="text-[#919191]/40">|</span>
            <button
              type="button"
              onClick={deselectAll}
              className="text-xs font-medium text-[#0077b5] hover:text-[#005a8c] transition-colors"
            >
              Deselect All
            </button>
          </div>
        )}
      </div>

      <div className="border border-[#919191]/30">
        <div className="max-h-40 overflow-y-auto">
          {loading && (
            <div className="px-4 py-3 space-y-2">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-8 bg-gray-200 animate-pulse" />
              ))}
            </div>
          )}

          {!loading && tree.assessments.map((a) => {
            const aKey = `a${KEY_SEP}${a.id}`;
            const aExpanded = expanded.has(aKey);
            return (
              <React.Fragment key={aKey}>
                <Row
                  state={assessmentState(a)}
                  onToggle={() => toggleAssessment(a)}
                  expandable={a.documents.length > 0}
                  expanded={aExpanded}
                  onToggleExpand={() => toggleExpanded(aKey)}
                  icon={<FiList className={`text-base flex-shrink-0 ${
                    assessmentState(a) === 'unchecked' ? 'text-[#919191]/50' : 'text-[#F47F2E]'
                  }`} />}
                  meta={`${a.answerCount} answers${a.documents.length ? ` • ${a.documents.length} docs` : ''}`}
                >
                  <span className="text-sm block truncate" title={a.label}>{a.label}</span>
                </Row>

                {aExpanded && a.documents.map((d) => (
                  <Row
                    key={`${aKey}${KEY_SEP}${d.key}`}
                    depth={1}
                    state={selectedDocumentKeys.has(d.key) ? 'checked' : 'unchecked'}
                    onToggle={() => toggleDocuments([d.key])}
                    icon={<FiFileText className="text-sm flex-shrink-0 text-[#919191]/70" />}
                    meta={d.docType || null}
                  >
                    <span className="text-xs block truncate" title={d.title}>{d.title}</span>
                    <span className="text-[11px] block text-[#919191]">{d.key}</span>
                  </Row>
                ))}
              </React.Fragment>
            );
          })}

          {/* Account / vendor evidence that isn't attached to any assessment. */}
          {!loading && tree.unboundDocuments.length > 0 && (() => {
            const uKey = 'unbound';
            const uExpanded = expanded.has(uKey);
            const keys = tree.unboundDocuments.map(d => d.key);
            return (
              <React.Fragment key={uKey}>
                <Row
                  state={groupState(keys, selectedDocumentKeys)}
                  onToggle={() => toggleDocuments(keys)}
                  expandable
                  expanded={uExpanded}
                  onToggleExpand={() => toggleExpanded(uKey)}
                  icon={<FiFolder className={`text-base flex-shrink-0 ${
                    groupState(keys, selectedDocumentKeys) === 'unchecked' ? 'text-[#919191]/50' : 'text-[#F47F2E]'
                  }`} />}
                  meta={`${keys.length} docs`}
                >
                  <span className="text-sm block">Other documents</span>
                </Row>
                {uExpanded && tree.unboundDocuments.map((d) => (
                  <Row
                    key={`${uKey}${KEY_SEP}${d.key}`}
                    depth={1}
                    state={selectedDocumentKeys.has(d.key) ? 'checked' : 'unchecked'}
                    onToggle={() => toggleDocuments([d.key])}
                    icon={<FiFileText className="text-sm flex-shrink-0 text-[#919191]/70" />}
                    meta={d.docType || null}
                  >
                    <span className="text-xs block truncate" title={d.title}>{d.title}</span>
                    <span className="text-[11px] block text-[#919191]">{d.key}</span>
                  </Row>
                ))}
              </React.Fragment>
            );
          })()}

          {!loading && !hasRows && (
            <div className="px-4 py-6 text-center">
              <p className="text-sm text-[#919191]">
                {!targetKey
                  ? 'Select an assessment to see its source data'
                  : error
                  ? 'Could not load source data'
                  : 'No source data available'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SourceDataSelector;
